import useGetMyAccountFeatures from '@/api/queries/me/useGetMyAccountFeatures';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import EstimateBuilderSidebar from '@/ui/pages/estimateBuilderPage/components/EstimateBuilderSidebar';
import AuthenticatedPageWrapper from '@/ui/pages/pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';
import {
  BreadcrumbItem,
  Button,
  Card,
  EmptyState,
  Sidebar,
} from '@payaca/ambrosia';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import EstimatePreviewPane from './components/EstimatePreviewPane';

type EstimateBuilderContentProps = {
  projectId: string;
  estimateId: string;
};

const EstimateBuilderContent: FC<EstimateBuilderContentProps> = (props) => {
  const { projectId, estimateId } = props;
  // Decide the mobile/desktop split on the viewport, not the main container.
  // The mobile preview uses the ambrosia Sidebar, which switches between a
  // full-width bottom sheet and a fixed-width side panel on the `md:` viewport
  // breakpoint. Keying this off the container caused a mismatch zone (e.g. a
  // ~777px viewport with a narrower container) where the page stayed in mobile
  // layout but the Sidebar rendered as a half-width side panel.
  const isMobile = !useIsContainerBeyondBreakpoint('md', {
    container: 'document',
  });
  const [isPanelOpen, setIsPanelOpen] = useState(!isMobile);

  // On mobile the preview is opened on demand via the button below; never let
  // it start open. The initial `!isMobile` can be momentarily stale before the
  // container is measured, which would otherwise pop the preview open over the
  // builder. Closing on the mobile transition leaves user-initiated opens
  // (which don't change `isMobile`) untouched.
  useEffect(() => {
    if (isMobile) setIsPanelOpen(false);
  }, [isMobile]);

  if (isMobile) {
    return (
      <div className="min-h-0 h-full flex flex-col">
        <EstimateBuilderSidebar projectId={projectId} estimateId={estimateId} />

        <div className="py-4 px-3.5 border-t">
          <Button
            className="w-full"
            variant="white"
            size="sm"
            onClick={() => setIsPanelOpen((open) => !open)}
          >
            Preview estimate
          </Button>
        </div>

        <Sidebar isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)}>
          <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
            <EstimatePreviewPane estimateId={estimateId} />
          </div>
        </Sidebar>
      </div>
    );
  }

  // cqw - container query width
  // very cool css unit that allows you to get the width of a container query.
  // the `<main>` element from AuthenticatedPageWrapper is set as a `@container` query.
  // So --panel-w:min(50cqw,600px) = 50% width of the main container up to 600px
  // We can use this css variable later in our markup using [var(--panel-w)]
  return (
    <div className="relative flex min-h-0 h-full bg-gray-50 [--panel-w:min(50cqw,600px)]">
      <div className="min-w-0 flex-1 flex overflow-y-auto p-8">
        <div className="max-w-[900px] w-full mx-auto">
          <EstimatePreviewPane estimateId={estimateId} />
        </div>
      </div>

      <div
        className={clstx(
          'absolute top-2 z-10 transition-[right] duration-300 ease-out',
          isPanelOpen ? 'right-[var(--panel-w)]' : 'right-0'
        )}
      >
        <Button
          className="rounded-r-none border-r-0"
          variant="white"
          onClick={() => setIsPanelOpen((open) => !open)}
        >
          <UntitledIcon
            name={isPanelOpen ? 'chevron-right' : 'chevron-left'}
            className="size-4"
          />
        </Button>
      </div>

      <div
        inert={!isPanelOpen}
        aria-hidden={!isPanelOpen}
        className={clstx(
          'bg-white overflow-hidden border-l shrink-0 transition-[width] duration-300 ease-out',
          isPanelOpen ? 'w-[var(--panel-w)]' : 'w-0'
        )}
      >
        <div className="h-full w-[var(--panel-w)]">
          <EstimateBuilderSidebar
            projectId={projectId}
            estimateId={estimateId}
          />
        </div>
      </div>
    </div>
  );
};

const EstimateBuilderPage: FC = () => {
  const { projectId, estimateId } = useParams<{
    projectId: string;
    estimateId: string;
  }>();
  const { data: features, isLoading: isLoadingFeatures } =
    useGetMyAccountFeatures();
  const hasEstimates = features?.includes('hasEstimates') ?? false;

  const { project, estimate, isLoading, isFetching } = useGetProjectEstimate(
    hasEstimates ? projectId : undefined,
    estimateId
  );

  // Estimates are gated on hasEstimates, mirroring the gate on the
  // Estimates tab in ProjectPage. Backend access policies enforce this
  // independently — the redirect avoids rendering an unusable page UI.
  if (!isLoadingFeatures && !hasEstimates) {
    return <Redirect to={`/projects/${projectId}`} />;
  }

  // Always render three crumbs so the trail length doesn't shift when
  // project metadata arrives. Crumbs without a `to` render as plain text
  // labels.
  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: project?.pipeline.name ?? 'Pipeline',
      to: project?.pipeline
        ? `/pipelines/${project.pipeline.publicId}`
        : undefined,
    },
    {
      label: project ? `Project: #${project.reference}` : 'Project',
      to: `/projects/${projectId}/estimates`,
    },
    {
      label: estimate ? `Estimate: ${estimate.reference}` : 'Estimate',
    },
  ];

  // Show the loader on the initial fetch and on background refetches that
  // haven't yet resolved the estimate. The latter avoids briefly flashing
  // "not found" after a cache-invalidating mutation (e.g. just-created
  // estimate not yet present in cache).
  if (isLoadingFeatures || isLoading || (isFetching && !estimate)) {
    return (
      <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
        <LoaderOverlay />
      </AuthenticatedPageWrapper>
    );
  }

  if (!estimate) {
    return (
      <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
        <Card>
          <Card.Body>
            <EmptyState text="Estimate not found." />
          </Card.Body>
        </Card>
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
      <EstimateBuilderContent projectId={projectId} estimateId={estimate.id} />
    </AuthenticatedPageWrapper>
  );
};

export default EstimateBuilderPage;
